/**
 * Computer players. chooseBotAction redacts the state for the bot before it
 * decides, so a bot can only use what that player could see at the table.
 * Deterministic: the bot's RNG seed is derived from the state, nothing mutates.
 */
import { COLS, deckComposition, partnerOf, SLOTS } from './deck';
import { faceDownSlots, IllegalActionError, legalActions } from './game';
import { redactFor } from './redact';
import { mixSeed, Rng } from './rng';
import { bonusFor, pairScore, scoreValues } from './scoring';
import type { Action, BotLevel, CardValue, GameState, PlayerState } from './types';

type Values = (CardValue | null)[];

/** Skips a bot allows itself per hole before it stops skipping (RULES.md 12). */
const SKIP_LIMIT: Record<BotLevel, number> = { easy: 0, medium: 3, hard: 4 };

/** Hard-bot tuning knobs (exported for experiments; not part of the public API). */
export const HARD_TUNING = {
  takeMargin: 0.25,
  oppGainUnknown: 2.5,
  oppGainKnown: 1,
  leadBonusBase: 1,
  leadBonusPer: 0.25,
  behindBase: 1,
  behindPer: 0.9,
  feedWeight: 0.35,
  feedFloor: 1,
};

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

export function chooseBotAction(s: GameState, playerIndex: number): Action {
  const me = s.players[playerIndex] as PlayerState | undefined;
  return decide(s, playerIndex, me?.botLevel ?? 'medium');
}

/** What a bot of `level` would do in this seat (the body of chooseBotAction, any level). */
function decide(s: GameState, playerIndex: number, level: BotLevel): Action {
  const legal = legalActions(s, playerIndex);
  const first = legal[0];
  if (!first) throw new IllegalActionError(`Player ${playerIndex} has no legal action`, 'notAllowed');
  if (legal.length === 1) return first;
  const rng = new Rng(mixSeed(s.rngState, s.seed, s.eventSeq, playerIndex, s.hole, s.holeTurn));
  // The bot only ever sees its own redacted view.
  const view = redactFor(s, playerIndex);
  const bot = new BotView(view, playerIndex, level, rng);
  let choice: Action;
  switch (level) {
    case 'easy':
      choice = bot.easy();
      break;
    case 'hard':
      choice = bot.hard();
      break;
    default:
      choice = bot.medium();
  }
  return legal.find((l) => sameAction(l, choice)) ?? first;
}

// ---------------------------------------------------------------------------
// Hints: the hard bot's move for a human seat, and why
// ---------------------------------------------------------------------------

/** Why the hint suggests what it does (the client turns this into one short line). */
export type HintReason =
  | 'deckOnly' | 'deckBetter' // draw stage, deck
  | 'takePair' | 'takeBonus' | 'takeLow' | 'takeBetter' | 'takeGood' // draw stage, discard pile
  | 'swapOut' | 'swapPair' | 'swapBonus' | 'swapHigh' | 'swapHidden' // holding, swap
  | 'discard' // holding, toss
  | 'flipOut' | 'flipPartner' | 'flipAny' | 'keepHidden'; // flip stage

export interface Suggestion {
  action: Action;
  reason: HintReason;
  /** The card the advice is about: the discard top or the held card. */
  value?: CardValue;
  /** The card it beats or pairs with: the 11 it replaces, the partner it could pair. */
  other?: CardValue;
}

/**
 * What the hard computer player would do in `player`'s seat, and why. Works on
 * the full state or on a state redacted for `player` (it redacts internally, like
 * chooseBotAction). Null when the player has no decision to make: not their turn,
 * tee off, hole over, game over.
 */
export function suggestAction(s: GameState, player: number): Suggestion | null {
  if (s.phase === 'initialFlip' || s.phase === 'holeOver' || s.phase === 'gameOver') return null;
  const legal = legalActions(s, player);
  if (legal.length === 0) return null;
  const action = decide(s, player, 'hard');
  const view = redactFor(s, player);
  const me = view.players[player] as PlayerState;
  return { action, ...classify(view, me, action, legal) };
}

/** Columns whose two values are both x. */
function matchedOf(vals: Values, x: CardValue): number {
  let n = 0;
  for (let c = 0; c < COLS; c++) if (vals[c] === x && vals[c + COLS] === x) n++;
  return n;
}

function placed(vals: Values, slot: number, x: CardValue): Values {
  const after = vals.slice();
  after[slot] = x;
  return after;
}

function classify(view: GameState, me: PlayerState, action: Action, legal: Action[]): Omit<Suggestion, 'action'> {
  const vals = valuesOf(me);
  const top = view.discardPile[view.discardPile.length - 1]?.value;
  const down = vals.filter((v) => v === null).length;
  switch (action.type) {
    case 'drawDeck':
      if (top === undefined || !legal.some((a) => a.type === 'drawDiscard')) return { reason: 'deckOnly' };
      return { reason: 'deckBetter', value: top };
    case 'drawDiscard': {
      const d = top as CardValue;
      let best = 0;
      for (let i = 0; i < SLOTS; i++) {
        if (vals[partnerOf(i)] === d && vals[i] !== d) best = Math.max(best, matchedOf(placed(vals, i, d), d));
      }
      if (best >= 2) return { reason: 'takeBonus', value: d };
      if (best === 1) return { reason: 'takePair', value: d };
      if (d <= 0) return { reason: 'takeLow', value: d };
      let hi: CardValue | null = null;
      for (let i = 0; i < SLOTS; i++) {
        const v = vals[i] ?? null;
        if (v === null || vals[partnerOf(i)] === v) continue;
        if (hi === null || v > hi) hi = v;
      }
      if (hi !== null && hi >= d + 3) return { reason: 'takeBetter', value: d, other: hi };
      return { reason: 'takeGood', value: d };
    }
    case 'discardDrawn':
      return { reason: 'discard', ...(view.drawn ? { value: view.drawn.value } : {}) };
    case 'swap': {
      const x = view.drawn?.value;
      const i = action.slot;
      const held = x !== undefined ? { value: x } : {};
      if (view.phase === 'playing' && down === 1 && vals[i] === null) return { reason: 'swapOut', ...held };
      if (x !== undefined && vals[partnerOf(i)] === x) {
        if (matchedOf(placed(vals, i, x), x) >= 2) return { reason: 'swapBonus', value: x };
        return { reason: 'swapPair', value: x, other: x };
      }
      const cur = vals[i] ?? null;
      if (cur !== null) return { reason: 'swapHigh', ...held, other: cur };
      return { reason: 'swapHidden', ...held };
    }
    case 'skip':
      return { reason: 'keepHidden' };
    case 'flip': {
      if (view.phase === 'playing' && down === 1) return { reason: 'flipOut' };
      const p = vals[partnerOf(action.slot)] ?? null;
      if (p !== null) return { reason: 'flipPartner', other: p };
      return { reason: 'flipAny' };
    }
    default:
      return { reason: 'flipAny' };
  }
}

function sameAction(a: Action, b: Action): boolean {
  if (a.type !== b.type) return false;
  const sa = 'slot' in a ? a.slot : undefined;
  const sb = 'slot' in b ? b.slot : undefined;
  const pa = 'player' in a ? a.player : undefined;
  const pb = 'player' in b ? b.player : undefined;
  return sa === sb && pa === pb;
}

// ---------------------------------------------------------------------------
// Unseen-card distribution
// ---------------------------------------------------------------------------

/** Multiset of cards this player has not seen (face-down anywhere + draw pile). */
export class Dist {
  readonly cnt = new Map<CardValue, number>();
  n = 0;
  sum = 0;
  private uu: number | null = null;

  constructor(view: GameState) {
    for (const [v, c] of deckComposition(view.options)) this.cnt.set(v, c);
    const seen = (v: CardValue): void => {
      this.cnt.set(v, (this.cnt.get(v) ?? 0) - 1);
    };
    for (const p of view.players) for (const sl of p.grid) if (sl.faceUp && sl.card) seen(sl.card.value);
    for (const c of view.discardPile) seen(c.value);
    if (view.drawn) seen(view.drawn.value);
    for (const [v, c] of this.cnt) {
      const k = Math.max(0, c);
      this.cnt.set(v, k);
      this.n += k;
      this.sum += k * v;
    }
  }

  mean(): number {
    return this.n > 0 ? this.sum / this.n : 5;
  }

  p(v: CardValue): number {
    return this.n > 0 ? (this.cnt.get(v) ?? 0) / this.n : 0;
  }

  /** Mean of an unseen card given it is not v. */
  meanExcept(v: CardValue): number {
    const c = this.cnt.get(v) ?? 0;
    const rest = this.n - c;
    return rest > 0 ? (this.sum - v * c) / rest : this.mean();
  }

  /** Expected score of a column with both cards unknown. */
  bothUnknown(): number {
    if (this.uu !== null) return this.uu;
    if (this.n < 2) return (this.uu = 2 * this.mean());
    let e = 0;
    for (const [x, c] of this.cnt) {
      if (c === 0) continue;
      const px = c / this.n;
      const q = (c - 1) / (this.n - 1);
      e += px * (q * pairScore(x) + (1 - q) * (x + this.meanExcept(x)));
    }
    return (this.uu = e);
  }

  values(): CardValue[] {
    return [...this.cnt].filter(([, c]) => c > 0).map(([v]) => v);
  }
}

function binom(n: number, k: number): number {
  let r = 1;
  for (let i = 1; i <= k; i++) r = (r * (n - k + i)) / i;
  return r;
}

/** Expected hole score of a grid where null = unknown card. */
export function expectedScore(values: Values, dist: Dist, opts: GameState['options']): number {
  if (values.length !== SLOTS) return 0;
  if (values.every((v) => v !== null)) return scoreValues(values, opts).total;
  let e = 0;
  const matched = new Map<CardValue, number>();
  const halves = new Map<CardValue, number>();
  for (let c = 0; c < COLS; c++) {
    const t = values[c] ?? null;
    const b = values[c + COLS] ?? null;
    if (t !== null && b !== null) {
      if (t === b) {
        e += pairScore(t);
        matched.set(t, (matched.get(t) ?? 0) + 1);
      } else e += t + b;
    } else if (t !== null || b !== null) {
      const v = (t ?? b) as CardValue;
      const p = dist.p(v);
      e += p * pairScore(v) + (1 - p) * (v + dist.meanExcept(v));
      halves.set(v, (halves.get(v) ?? 0) + 1);
    } else {
      e += dist.bothUnknown();
    }
  }
  const valuesWithPairs = new Set([...matched.keys(), ...halves.keys()]);
  for (const v of valuesWithPairs) {
    const k = matched.get(v) ?? 0;
    const h = halves.get(v) ?? 0;
    if (k + h < 2) continue;
    const p = dist.p(v);
    for (let j = 0; j <= h; j++) {
      const pr = binom(h, j) * p ** j * (1 - p) ** (h - j);
      e += pr * bonusFor(k + j);
    }
  }
  return e;
}

// ---------------------------------------------------------------------------
// The bot's view of the table
// ---------------------------------------------------------------------------

class BotView {
  readonly me: PlayerState;
  readonly mine: Values;
  private distCache: Dist | null = null;

  constructor(
    readonly s: GameState,
    readonly pi: number,
    readonly level: BotLevel,
    readonly rng: Rng,
  ) {
    this.me = s.players[pi] as PlayerState;
    this.mine = valuesOf(this.me);
  }

  get dist(): Dist {
    return (this.distCache ??= new Dist(this.s));
  }

  get legal(): Action[] {
    return legalActions(this.s, this.pi);
  }

  get discardTop(): CardValue | null {
    const c = this.s.discardPile[this.s.discardPile.length - 1];
    return c ? c.value : null;
  }

  get faceDown(): number[] {
    return faceDownSlots(this.me);
  }

  canSkip(): boolean {
    return this.legal.some((a) => a.type === 'skip');
  }

  canDrawDeck(): boolean {
    return this.legal.some((a) => a.type === 'drawDeck');
  }

  opponents(): number[] {
    return this.s.active.filter((i) => i !== this.pi);
  }

  /** Who draws right after me, if anyone this hole. */
  nextPlayer(): number | null {
    if (this.s.phase === 'finalTurns') {
      const rest = this.s.turnsLeftInFinal.filter((i) => i !== this.pi);
      return rest[0] ?? null;
    }
    const n = this.s.players.length;
    for (let k = 1; k < n; k++) {
      const i = (this.pi + k) % n;
      if (this.s.active.includes(i)) return i;
    }
    return null;
  }

  /** Slot where x would complete a vertical pair (partner face up with x, this slot not already x). */
  pairSlots(x: CardValue, values: Values = this.mine): number[] {
    const out: number[] = [];
    for (let slot = 0; slot < SLOTS; slot++) {
      const partner = values[partnerOf(slot)];
      if (partner === x && values[slot] !== x) out.push(slot);
    }
    return out;
  }

  /** Face-up slots not part of a matched column, highest value first. */
  unpairedFaceUp(values: Values = this.mine): number[] {
    const out: number[] = [];
    for (let slot = 0; slot < SLOTS; slot++) {
      const v = values[slot];
      if (v === null || v === undefined) continue;
      if (values[partnerOf(slot)] === v) continue;
      out.push(slot);
    }
    return out.sort((a, b) => (values[b] as number) - (values[a] as number));
  }

  wouldGoOut(slot: number): boolean {
    return this.s.phase === 'playing' && this.faceDown.length === 1 && this.faceDown[0] === slot;
  }

  /** Simple estimate: face-up cards exactly, each unknown at 5 (RULES.md 12). */
  roughScore(values: Values): number {
    const known = scoreValues(values, this.s.options).total;
    return known + values.filter((v) => v === null).length * 5;
  }

  /** Lowest rough estimate among opponents, minus what their last shot will likely gain. */
  bestOpponentRough(): number {
    const ops = this.opponents();
    if (ops.length === 0) return Infinity;
    return Math.min(...ops.map((o) => this.roughScore(valuesOf(this.s.players[o] as PlayerState)) - 2));
  }

  // -------------------------------------------------------------------------
  // Initial flip
  // -------------------------------------------------------------------------

  initialFlip(style: 'random' | 'sameColumn' | 'spread'): Action {
    const down = this.faceDown;
    const up = this.mine.flatMap((v, i) => (v !== null ? [i] : []));
    let slot: number;
    if (style === 'random' || up.length === 0) {
      if (style === 'sameColumn' && up.length === 0) slot = this.rng.int(COLS) + (this.rng.chance(0.5) ? COLS : 0);
      else slot = this.rng.pick(down);
    } else if (style === 'sameColumn') {
      slot = partnerOf(up[0] as number);
    } else {
      const usedCols = new Set(up.map((i) => i % COLS));
      const options = down.filter((i) => !usedCols.has(i % COLS));
      slot = this.rng.pick(options.length ? options : down);
    }
    if (!down.includes(slot)) slot = this.rng.pick(down);
    return { type: 'flipInitial', player: this.pi, slot };
  }

  // -------------------------------------------------------------------------
  // EASY: mostly random-but-legal, with simple sense
  // -------------------------------------------------------------------------

  easy(): Action {
    const s = this.s;
    const pi = this.pi;
    if (s.phase === 'initialFlip') return this.initialFlip('random');
    if (s.phase === 'holeOver') return { type: 'nextHole' };
    const legal = this.legal;
    // Some turns are just a whim, but an easy bot still won't take a high discard.
    if (this.rng.chance(0.3)) {
      const whim = this.rng.pick(legal);
      const d = this.discardTop;
      if (!(whim.type === 'drawDiscard' && d !== null && d >= 7)) return whim;
    }
    if (s.stage === 'draw') {
      const d = this.discardTop;
      if (d !== null && (d <= 2 || this.pairSlots(d).length > 0)) return { type: 'drawDiscard', player: pi };
      return this.canDrawDeck() ? { type: 'drawDeck', player: pi } : { type: 'drawDiscard', player: pi };
    }
    if (s.stage === 'holding') {
      const x = (s.drawn as { value: number }).value;
      const pairs = this.pairSlots(x);
      if (pairs.length > 0) return { type: 'swap', player: pi, slot: this.rng.pick(pairs) };
      const high = this.unpairedFaceUp()[0];
      if (x <= 4) {
        if (high !== undefined && (this.mine[high] as number) > x + 2) return { type: 'swap', player: pi, slot: high };
        if (this.faceDown.length > 0) return { type: 'swap', player: pi, slot: this.rng.pick(this.faceDown) };
      }
      if (s.drawnFrom === 'deck' && this.faceDown.length > 0) return { type: 'discardDrawn', player: pi };
      if (high !== undefined && (this.mine[high] as number) > x) return { type: 'swap', player: pi, slot: high };
      return { type: 'swap', player: pi, slot: this.rng.pick(this.faceDown.length ? this.faceDown : [0]) };
    }
    return { type: 'flip', player: pi, slot: this.rng.pick(this.faceDown) };
  }

  // -------------------------------------------------------------------------
  // MEDIUM: greedy heuristics
  // -------------------------------------------------------------------------

  /** Going out now would leave me behind someone. */
  mediumBehindIfOut(valuesAfter: Values): boolean {
    return this.roughScore(valuesAfter) > this.bestOpponentRough();
  }

  /** Best placement for x by the medium rules, or null to discard (deck draws only). */
  mediumPlacement(x: CardValue): number | null {
    const v = this.mine;
    const pairs = this.pairSlots(x);
    if (pairs.length > 0) {
      // Prefer replacing a face-down card or the highest known one.
      return pairs.sort((a, b) => (v[b] ?? 5.5) - (v[a] ?? 5.5))[0] as number;
    }
    const unpaired = this.unpairedFaceUp();
    const high = unpaired[0];
    const highVal = high !== undefined ? (v[high] as number) : -Infinity;
    if (x <= 4) {
      if (high !== undefined && highVal > x + 1 && highVal >= 6) return high;
      const down = this.faceDown;
      if (down.length > 0) {
        // Prefer a face-down slot whose partner is also face-down (keeps both chances open).
        const open = down.filter((i) => v[partnerOf(i)] === null);
        return this.rng.pick(open.length ? open : down);
      }
      if (high !== undefined && highVal > x) return high;
      return null;
    }
    if (high !== undefined && x < highVal - 2) return high;
    return null;
  }

  medium(): Action {
    const s = this.s;
    const pi = this.pi;
    if (s.phase === 'initialFlip') return this.initialFlip('sameColumn');
    if (s.phase === 'holeOver') return { type: 'nextHole' };

    if (s.stage === 'draw') {
      const d = this.discardTop;
      if (d !== null) {
        const slot = this.mediumPlacement(d);
        const useful = slot !== null && (this.pairSlots(d).length > 0 || d <= 3);
        if ((useful && !this.badGoOut(slot, d)) || !this.canDrawDeck()) return { type: 'drawDiscard', player: pi };
      }
      return { type: 'drawDeck', player: pi };
    }

    if (s.stage === 'holding') {
      const x = (s.drawn as { value: number }).value;
      let slot = this.mediumPlacement(x);
      if (slot !== null && this.badGoOut(slot, x)) {
        // Do not go out behind: improve a face-up card instead, or pass the card on.
        const alt = this.unpairedFaceUp().find((i) => (this.mine[i] as number) > x);
        slot = alt ?? null;
      }
      if (slot === null && s.drawnFrom === 'deck' && this.faceDown.length > 0) return { type: 'discardDrawn', player: pi };
      if (slot === null) {
        // Holding a discard card: must swap. Replace the highest unpaired card, else a face-down one.
        const high = this.unpairedFaceUp()[0];
        const down = this.faceDown.filter((i) => !this.wouldGoOut(i));
        slot = high !== undefined && (this.mine[high] as number) > x ? high : (down[0] ?? high ?? this.faceDown[0] ?? 0);
      }
      return { type: 'swap', player: pi, slot };
    }

    // flip stage
    const down = this.faceDown;
    if (down.length === 1 && this.canSkip()) {
      const behind = s.phase === 'playing' && this.mediumBehindIfOut(this.mine.map((v, i) => (i === down[0] ? 5 : v)));
      if (behind && this.me.skipsThisHole < SKIP_LIMIT.medium) return { type: 'skip', player: pi };
    }
    // Flip where the partner is known and high (find out if the column needs fixing).
    const scored = down.map((i) => ({ i, pv: this.mine[partnerOf(i)] ?? -1 }));
    scored.sort((a, b) => b.pv - a.pv);
    return { type: 'flip', player: pi, slot: (scored[0] as { i: number }).i };
  }

  /** Medium: would placing x in slot go out while behind? */
  private badGoOut(slot: number | null, x: CardValue): boolean {
    if (slot === null || !this.wouldGoOut(slot)) return false;
    const after = this.mine.slice();
    after[slot] = x;
    return this.mediumBehindIfOut(after);
  }

  // -------------------------------------------------------------------------
  // HARD: expected value over the unseen cards
  // -------------------------------------------------------------------------

  ev(values: Values): number {
    return expectedScore(values, this.dist, this.s.options);
  }

  /** Expected final score of an opponent after their remaining last shot. */
  private oppExpected(o: number): number {
    const p = this.s.players[o] as PlayerState;
    const vals = valuesOf(p);
    const unknown = vals.filter((v) => v === null).length;
    return this.ev(vals) - (unknown > 0 ? HARD_TUNING.oppGainUnknown : HARD_TUNING.oppGainKnown);
  }

  /**
   * Adjustment for an action that puts all my cards face up during normal
   * play (the hole ends after everyone else's last shot). `mine` is my
   * (expected) score after the action.
   */
  goOutAdj(mine: number): number {
    if (this.s.phase !== 'playing') return 0;
    const ops = this.opponents();
    if (ops.length === 0) return 0;
    const best = Math.min(...ops.map((o) => this.oppExpected(o)));
    const lead = best - mine;
    const t = HARD_TUNING;
    if (lead >= 0) return -t.leadBonusBase - Math.min(lead, 6) * t.leadBonusPer;
    return t.behindBase + -lead * t.behindPer;
  }

  /** How much handing card value c to the next player helps them (penalty for me). */
  private feed(c: CardValue | null): number {
    if (c === null) return 0;
    const np = this.nextPlayer();
    if (np === null) return 0;
    const p = this.s.players[np] as PlayerState;
    const vals = valuesOf(p);
    const before = this.ev(vals);
    let best = before;
    for (let slot = 0; slot < SLOTS; slot++) {
      const after = vals.slice();
      after[slot] = c;
      best = Math.min(best, this.ev(after));
    }
    const gain = before - best;
    return gain > HARD_TUNING.feedFloor ? (gain - HARD_TUNING.feedFloor) * HARD_TUNING.feedWeight : 0;
  }

  private skipCost(): number {
    return this.me.skipsThisHole >= SKIP_LIMIT.hard ? Infinity : 0.4 + this.me.skipsThisHole * 0.3;
  }

  /** Value (lower is better) of placing x into slot. */
  private swapValue(x: CardValue, slot: number): number {
    const after = this.mine.slice();
    after[slot] = x;
    let v = this.ev(after) + this.feed(this.mine[slot] ?? null);
    if (this.wouldGoOut(slot)) v += this.goOutAdj(this.ev(after));
    return v;
  }

  /** Value of discarding x (deck draws only) then flipping or skipping optimally. */
  private discardValue(x: CardValue): number {
    const cur = this.ev(this.mine);
    const feed = this.feed(x);
    const down = this.faceDown.length;
    if (down === 0) return Infinity;
    if (down === 1) {
      const flip = cur + this.goOutAdj(cur);
      const skip = this.canSkipAfterDiscard() ? cur + this.skipCost() : Infinity;
      return Math.min(flip, skip) + feed;
    }
    return cur + feed;
  }

  private canSkipAfterDiscard(): boolean {
    return this.s.phase === 'playing' || (this.s.phase === 'finalTurns' && this.s.options.skipOnFinalTurns);
  }

  private bestHold(x: CardValue, fromDeck: boolean): { value: number; slot: number | null } {
    let best = { value: Infinity, slot: null as number | null };
    for (let slot = 0; slot < SLOTS; slot++) {
      const v = this.swapValue(x, slot);
      if (v < best.value - 1e-9) best = { value: v, slot };
    }
    if (fromDeck && this.faceDown.length > 0) {
      const d = this.discardValue(x);
      if (d < best.value - 1e-9) best = { value: d, slot: null };
    }
    return best;
  }

  hard(): Action {
    const s = this.s;
    const pi = this.pi;
    if (s.phase === 'initialFlip') return this.initialFlip('spread');
    if (s.phase === 'holeOver') return { type: 'nextHole' };

    if (s.stage === 'draw') {
      const d = this.discardTop;
      if (!this.canDrawDeck()) return { type: 'drawDiscard', player: pi };
      if (d === null) return { type: 'drawDeck', player: pi };
      const take = this.bestHold(d, false).value;
      const dist = this.dist;
      let deck = 0;
      for (const x of dist.values()) deck += dist.p(x) * this.bestHold(x, true).value;
      return take < deck - HARD_TUNING.takeMargin ? { type: 'drawDiscard', player: pi } : { type: 'drawDeck', player: pi };
    }

    if (s.stage === 'holding') {
      const x = (s.drawn as { value: number }).value;
      const best = this.bestHold(x, s.drawnFrom === 'deck');
      if (best.slot === null) return { type: 'discardDrawn', player: pi };
      return { type: 'swap', player: pi, slot: best.slot };
    }

    // flip stage
    const down = this.faceDown;
    if (down.length === 1) {
      const cur = this.ev(this.mine);
      const flip = cur + this.goOutAdj(cur);
      const skip = this.canSkip() ? cur + this.skipCost() : Infinity;
      if (skip < flip) return { type: 'skip', player: pi };
      return { type: 'flip', player: pi, slot: down[0] as number };
    }
    // Flip next to a known card: highest partner first (learn whether that column needs fixing).
    const withPartner = down.filter((i) => this.mine[partnerOf(i)] !== null);
    if (withPartner.length > 0) {
      withPartner.sort((a, b) => (this.mine[partnerOf(b)] as number) - (this.mine[partnerOf(a)] as number));
      return { type: 'flip', player: pi, slot: withPartner[0] as number };
    }
    return { type: 'flip', player: pi, slot: this.rng.pick(down) };
  }
}

function valuesOf(p: PlayerState): Values {
  return p.grid.map((sl) => (sl.faceUp && sl.card ? sl.card.value : null));
}
